import { useHandler } from "@redotech/react-util/hook";
import { useOptimizationSettings } from "@redotech/redo-merchant-app-common/support/use-optimization-settings";
import { ShopperAiSettingsOptimizationType } from "@redotech/redo-model/shopper-ai-settings-optimization";
import { toast } from "@redotech/redo-web/alert";
import { RedoToggle } from "@redotech/redo-web/arbiter-components/toggle/redo-toggle";
import { memo } from "react";
import {
  GenericSettingsOptimizationBanner,
  SettingToOptimize,
} from "../optimization-banners/generic-settings-optimization-banner";
import {
  checkBaseEligibility,
  OptimizationProps,
  SettingsOptimizationDefinition,
} from "../optimization-types";

const ProactivePromptsOptimizationBanner = memo(
  function ProactivePromptsOptimizationBanner({
    handleComplete,
    handleDismiss,
  }: OptimizationProps) {
    const {
      settings,
      mutation,
      handleApplyAll,
      componentLevelDismiss,
      isLoading,
    } = useOptimizationSettings({
      applyAllRecommendations: { "concierge/proactiveChipEngagement": true },
      handleComplete,
      handleDismiss,
      shouldCompleteOnAnyChange: true,
    });

    const handleUpdate = useHandler(async (enabled: boolean) => {
      await mutation.mutateAsync({
        "concierge/proactiveChipEngagement": enabled,
      });
      toast("Settings applied successfully", { variant: "success" });
    });

    if (isLoading || !settings) {
      return null;
    }

    const settingsConfig: SettingToOptimize[] = [
      {
        title: "Enable proactive prompt engagement",
        description:
          "Show suggested conversation starting prompts to customers when they open the chat widget increasing engagement and intent to buy.",
        control: (
          <RedoToggle
            setValue={handleUpdate}
            value={settings["concierge/proactiveChipEngagement"] ?? false}
          />
        ),
        onUpdate: () =>
          handleUpdate(settings["concierge/proactiveChipEngagement"] ?? false),
      },
    ];

    return (
      <GenericSettingsOptimizationBanner
        handleComplete={handleComplete}
        handleDismiss={componentLevelDismiss}
        onApplyAll={handleApplyAll}
        settings={settingsConfig}
      />
    );
  },
);

export const proactivePromptsOptimization: SettingsOptimizationDefinition = {
  type: ShopperAiSettingsOptimizationType.PROACTIVE_PROMPTS_OPTIMIZATION,
  priority: 0,
  retries: 2,
  isEligible: async (team, user) => {
    if (!checkBaseEligibility(user)) {
      return false;
    }

    return (
      team.settings.concierge?.enabled === true &&
      team.settings.concierge?.proactiveChipEngagement !== true
    );
  },
  component: ProactivePromptsOptimizationBanner,
};
